import { useCallback, useEffect, useRef, useState } from "react";

export const usePaginatedOfficeOptions = (fetchPage, { lazy = false } = {}) => {
  const [options, setOptions] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const searchTermRef = useRef("");
  const requestSeqRef = useRef(0);
  const inFlightRef = useRef(false);

  const load = useCallback(
    async (searchTerm, pageNum, append) => {
      const seq = ++requestSeqRef.current;
      inFlightRef.current = true;
      append ? setLoadingMore(true) : setLoading(true);
      try {
        const res = await fetchPage(searchTerm, pageNum);
        if (seq !== requestSeqRef.current) return; // a newer search superseded this one
        if (res?.status) {
          setOptions((prev) => (append ? [...prev, ...res.data] : res.data));
          setHasMore(!!res.has_more);
          setPage(pageNum);
        }
      } finally {
        if (seq === requestSeqRef.current) {
          inFlightRef.current = false;
          // Clear both: a search can supersede an in-flight load-more, whose finally then skips.
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [fetchPage],
  );

  useEffect(() => {
    if (!lazy) load("", 1, false);
  }, [lazy, load]);

  const search = useCallback(
    (term) => {
      searchTermRef.current = term;
      load(term, 1, false);
    },
    [load],
  );

  const loadMore = useCallback(() => {
    if (!hasMore || inFlightRef.current) return;
    load(searchTermRef.current, page + 1, true);
  }, [hasMore, page, load]);

  return { options, hasMore, loading, loadingMore, search, loadMore };
};

export default usePaginatedOfficeOptions;
